/**
 * Report analyses (migration 0096) — the persisted, scope-keyed analysis that is the SINGLE
 * source of truth a generated report renders AND its AI insight cites, computed once server-side
 * from a LIVE twin query for the window. Generating a report POSTs here to persist + get the
 * shared facts (fixing volatility); the CSV export gives the flat tables. Mirrors
 * internal/twin/report_analyses.go + factspack.go.
 */

import { api } from "@/lib/api-client";
import type { InsightReportKind } from "./insight-jobs";

/** One correlation fact (mirrors factspack.go corrFactf). CI arrays hold [lo,hi], either null. */
export interface CorrFact {
  pair: string;
  x: string;
  y: string;
  mechanism?: string;
  r: number | null;
  n: number;
  effective_n?: number;
  ci95_effective?: (number | null)[];
  ci_bonferroni?: (number | null)[];
  survives_multiplicity?: boolean;
  insufficient?: boolean;
  ci_straddles_zero?: boolean;
}

/**
 * S3.3 — the provenance sidecar. Every numeric leaf in the pack is either labelled in
 * `by_path` (collapsed JSON path → closed-vocabulary label) or listed in `withheld` (exact
 * path → why it is blank). The server DERIVES this by walking the marshalled payload, so a
 * number cannot reach the browser without an entry; a path in `withheld` has NO value and
 * must render as an em dash with its reason, never as 0.
 * Mirrors internal/twin/provenance.go + factspack_provenance.go.
 */
export interface FactsProvenance {
  invariant: string;
  vocabulary: string[];
  by_path: Record<string, string>;
  withheld?: Record<string, string>;
  notes?: string[];
}

/** Counts of the readings the pack's statistics are computed OVER, by the datum's own class
 *  (migration 0099). The queries do not filter data_class, so a pack whose readings are
 *  `simulated` carries statistics of a simulation — this is where that is stated. */
export interface ReadingsDataClassMix {
  real: number;
  simulated: number;
  imported: number;
  derived: number;
  unstated: number;
  total: number;
}

/** The statistical facts pack shape (only the fields the panel renders are typed). */
export interface ReportFacts {
  kind?: string;
  generated?: string;
  provenance?: FactsProvenance;
  readings_data_class_mix?: ReadingsDataClassMix;
  rejection_summary?: {
    produced: number;
    scrapped: number;
    rejection_pct: number;
    yield_pct: number;
  };
  capability_findings?: Array<{
    parameter: string;
    control_band: (number | null)[];
    n_readings: number;
    mean: number;
    sd: number;
    pct_outside_band: number;
    pct_below_band: number;
    pct_above_band: number;
    pct_outside_ci95?: (number | null)[];
  }>;
  correlations_rejection_drivers?: CorrFact[];
  correlations_sand_control?: CorrFact[];
  defect_pareto?: Array<{ defect: string; lots: number; family: string; sand_admissible: string }>;
  defect_family_share?: {
    top8_lots: number;
    sand_admissible_lots: number;
    sand_admissible_pct_of_top8: number | null;
  };
  [k: string]: unknown;
}

export interface ReportAnalysis {
  analysis_id: string;
  facts: ReportFacts;
  generated_at: string;
}

export const reportAnalyses = {
  /** Compute + persist the analysis for a report scope, and return the shared facts.
   *  `POST /v1/twin/report-analyses`. 501 when the kind is not computed server-side yet. */
  compute: (body: { kind: InsightReportKind; window?: { from?: string; to?: string }; tags?: string[] }) =>
    api.post<ReportAnalysis>("/v1/twin/report-analyses", body),
  /** Download the analysis as a flat CSV. `GET …/{id}/csv` → Blob. */
  downloadCsv: (id: string) => api.getBlob(`/v1/twin/report-analyses/${id}/csv`),
};
